/**
 * Fast location utilities - Optimized location handling for modern URLs
 * Includes city detection for address validation and user address selection
 */

import { getCityDetailFn } from '@/api/ahafood/get-city-detail-fn';
import {
  CITIES,
  DEFAULT_CITY as DEFAULT_CITY_CONFIG,
} from '@/lib/config/cities';

export interface CityInfo {
  slug: string;
  value: string;
  id: string; // alias kept for backward compat — same as value
  label: string;
}

/**
 * Default city used whenever detection cannot resolve a supported city
 * (API error, or coordinates in a city not yet listed in CITIES). Guarantees
 * city_id is ALWAYS present. Derived from lib/config/cities — the single
 * default for the whole app.
 */
export const DEFAULT_CITY: CityInfo = {
  slug: DEFAULT_CITY_CONFIG.slug,
  value: DEFAULT_CITY_CONFIG.value,
  id: DEFAULT_CITY_CONFIG.value,
  label: DEFAULT_CITY_CONFIG.label,
};

/**
 * Map a city_id (from the city_detail API) to a supported CityInfo.
 * Returns null when the city_id is not in CITIES (not yet supported).
 */
export function findCityByValue(cityId?: string | null): CityInfo | null {
  if (!cityId) return null;
  const city = CITIES.find((c) => c.value === cityId);
  if (!city) return null;
  return {
    slug: city.slug,
    value: city.value,
    id: city.value,
    label: city.label,
  };
}

/**
 * Extract lat/lng from URL search params
 * @param searchParams - URL search parameters
 * @returns Object with lat, lng, and hasLocation flag
 */
export function extractLocationFromUrlParams(searchParams: {
  lat?: string | string[];
  lng?: string | string[];
}): {
  lat?: number;
  lng?: number;
  hasLocation: boolean;
} {
  const latParam = Array.isArray(searchParams.lat)
    ? searchParams.lat[0]
    : searchParams.lat;
  const lngParam = Array.isArray(searchParams.lng)
    ? searchParams.lng[0]
    : searchParams.lng;

  if (latParam && lngParam) {
    const lat = parseFloat(latParam);
    const lng = parseFloat(lngParam);

    if (!isNaN(lat) && !isNaN(lng)) {
      return { lat, lng, hasLocation: true };
    }
  }

  return { hasLocation: false };
}

/**
 * Create location params object for API calls
 * @param lat Latitude
 * @param lng Longitude
 * @param defaultRadius Default radius if not provided
 * @returns Object with lat, lng, and radius for API calls
 */
export function createLocationParams(
  lat?: number,
  lng?: number,
  defaultRadius = 10000
): { lat?: number; lng?: number; radius?: number } {
  if (lat && lng) {
    return { lat, lng, radius: defaultRadius };
  }
  return {};
}

/**
 * Generate place_id from coordinates (for backward compatibility)
 * @param lat Latitude
 * @param lng Longitude
 * @returns Place ID string
 */
export function generatePlaceId(lat: number, lng: number): string {
  return `${lat}_${lng}`;
}

/**
 * Resolve the city of a coordinate via Ahamove's `city_detail` API.
 *
 * Replaces the legacy haversine/zone-radius detection. The API returns the
 * authoritative city_id, which is mapped to a supported CityInfo (for routing
 * slug). Falls back to {@link DEFAULT_CITY} when the API fails or the resolved
 * city is not yet listed in CITIES — so city_id is ALWAYS present.
 */
export async function resolveCityFromCoordinates(
  lat: number,
  lng: number
): Promise<CityInfo> {
  try {
    const detail = await getCityDetailFn(lat, lng);
    // Prefer the service city id; fall back to the raw _id.
    const cityId = detail?.service_city_id ?? detail?._id;
    const matched = findCityByValue(cityId);
    if (matched) return matched;
  } catch (error) {
    console.warn('resolveCityFromCoordinates failed:', error);
  }

  return DEFAULT_CITY;
}

/**
 * Find city info from CITIES array by slug
 * @param citySlug City slug to search for
 * @returns City from CITIES array or null
 */
export function findCityBySlug(citySlug: string) {
  return CITIES.find((city) => city.slug === citySlug);
}

/**
 * Detect city from address text (fallback method)
 * @param address Address string to search in
 * @returns Matched city from CITIES array or null
 */
export function detectCityFromAddressText(address?: string) {
  if (!address) return null;
  return CITIES.find((city) => address.includes(city.label));
}

/**
 * Calculate distance between two coordinates using Haversine formula
 * @param point1 First coordinate {lat, lng}
 * @param point2 Second coordinate {lat, lng}
 * @returns Distance in meters
 */
export function calculateDistance(
  point1: { lat: number; lng: number },
  point2: { lat: number; lng: number }
): number {
  const R = 6371000; // Earth radius in meters
  const dLat = ((point2.lat - point1.lat) * Math.PI) / 180;
  const dLng = ((point2.lng - point1.lng) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((point1.lat * Math.PI) / 180) *
      Math.cos((point2.lat * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Create address object from coordinates
 * @param lat Latitude
 * @param lng Longitude
 * @param address Optional address string
 * @returns Basic address object
 */
export function createBasicAddress(lat: number, lng: number, address?: string) {
  return {
    lat,
    lng,
    address: address ?? `${lat}, ${lng}`,
    place_id: generatePlaceId(lat, lng),
    second_label: address ?? 'Vị trí của bạn',
  };
}
